// utils/generatePrescriptionImage.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";

const escapeHtml = (unsafe) => {
  if (typeof unsafe !== "string") return unsafe;
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/\//g, "&#x2F;");
};

const getPrescriptionTitle = (prescriptionType) => {
  switch (prescriptionType) {
    case "private":
      return "Private Prescription";
    case "pgd":
      return "PGD";
    case "pharmacy-first":
      return "Pharmacy First";
    default:
      return "Private e-Prescription";
  }
};
const getItemsSectionTitle = (prescriptionType) => {
  if (prescriptionType === "pgd") {
    return "Items to be supplied";
  }
  return "💊 Prescribed Medications";
};

export const generatePrescriptionImageBase64 = async ({
  data,
  templateHtmlId = "hidden-prescription-html",
}) => {
  // Create a hidden div
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.fontFamily = "Arial, Helvetica, sans-serif";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.margin = "20px";
  container.style.padding = "20px";
  container.style.borderRadius = "15px";
  container.id = templateHtmlId;
  try {
    container.innerHTML = generatePrescriptionHTML(data); // use template data to render HTML
  } catch (error) {
    console.error("Error generating prescription HTML:", error);
    container.innerHTML = "";
  }
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 1.25,
      useCORS: true,
    });

    const imageData = canvas.toDataURL("image/png");

    // Optional PDF logic
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imageData, "PNG", 0, 0, imgWidth, imgHeight);
    // pdf.save("Prescription.pdf");

    // Clean up
    document.body.removeChild(container);

    return {
      base64Image: imageData,
      expiryDate: moment().add(6, "months").format("YYYY-MM-DD"),
    };
  } catch (error) {
    console.error(
      "Failed to generate image from prescription template:",
      error,
    );
    document.body.removeChild(container);
    return null;
  }
};

const generatePrescriptionHTML = (data) => {
  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "No date";
    }

    const pad = (value) => String(value).padStart(2, "0");

    if (typeof dateValue === "string") {
      const trimmedValue = dateValue.trim();

      if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmedValue)) {
        const [day, month, year] = trimmedValue.split(/[\/\s,]/).filter(Boolean);
        return `${pad(day)}/${pad(month)}/${year}`;
      }

      if (/^\d{4}-\d{2}-\d{2}/.test(trimmedValue)) {
        const [year, month, day] = trimmedValue.split("-");
        return `${pad(day)}/${pad(month)}/${year}`;
      }
    }

    const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
    const isValid = !Number.isNaN(parsedDate.getTime());

    if (!isValid) {
      console.error("Invalid date:", dateValue);
      return "Invalid date";
    }

    const day = pad(parsedDate.getDate());
    const month = pad(parsedDate.getMonth() + 1);
    const year = parsedDate.getFullYear();

    return `${day}/${month}/${year}`;
  };
  const pharmacistInstructionsHTML = (data.pharmacistInstructions || [])
    .map((instruction, index) => `<li>${instruction ?? "Unknown"}</li>`)
    .join("");
  return `
<div class="prescription-container" style="
            border: 2px solid #e5e7eb !important;
            border-radius: 8px !important;
            max-width: 100% !important;
            max-height: 100% !important;
            page-break-inside: avoid !important;
            display: block !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
            ">
    <div class="watermark" style="display: none !important;">PRESCRIPTION</div>
    <div class="clinic-header" style="display: flex;
            align-items: center;
            background: #f8fafc;
            padding: 20px 25px;
            border-bottom: 2px solid #e5e7eb;
            position: relative;
            z-index: 2;">
        <img alt=${
          escapeHtml(data?.company?.companyLogo) || ""
        } class="clinic-logo"
            src=${escapeHtml(data?.company?.companyLogo) || ""}
            style="width: 60px;
            height: 60px;
            margin-right: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(30, 64, 175, 0.15);
            background: #ffffff;
            padding: 8px;" />
        <div class="clinic-info" style="flex-grow: 1;">
            <h2 style="font-size: 20px;
            font-weight: 700;
            color: #1e40af;
            margin-bottom: 4px;
            letter-spacing: -0.5px;">${escapeHtml(
              data?.pharmacyDetails.teamPharmacyName,
            )}
            </h2>
            <div class="address" style="font-size: 13px;
            color: #4b5563;
            margin-bottom: 2px;
            font-weight: 400;">${
              escapeHtml(data.pharmacyDetails?.teamPharmacyAddress) || ""
            }
            </div>
            <div class="contact" style="font-size: 12px;
            color: #6b7280;
            font-weight: 400;">📧 ${
              escapeHtml(data.pharmacyContactDetails?.emailAddress) || ""
            } • 📞 ${escapeHtml(data.pharmacyContactDetails?.phoneNumber) || ""}
            </div>
        </div>
        <div class="prescription-number" style="text-align: right;
            color: #4b5563;
            font-size: 12px;">
            <div>Prescription No.</div>
            <div class="number" style="font-family: 'JetBrains Mono', monospace;
            font-weight: 600;
            color: #1e40af;
            font-size: 14px;">RX-${data?.authorizationCode}
            </div>
        </div>
    </div>
    <div class="header" style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          color: #fff;
          padding: 16px 25px;
          position: relative;
          z-index: 2;">
        <h1 style="font-size: 22px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          text-align: center;
          color: #fff;
          margin: 0;">${getPrescriptionTitle(data?.prescriptionType)}
        </h1>
    </div>
    <div class="section" style="padding: 18px 25px;
                    border-bottom: 1px solid #e5e7eb;
                    position: relative;
                    z-index: 2;">
        <div class="section-title" style="font-size: 15px;
                    display: flex; align-items: center; gap: 8px;
                    color: #1e40af;
                    font-weight: 600;
                    margin-bottom: 12px;
                    padding-bottom: 6px;
                    border-bottom: 2px solid #f0f9ff;
                    display: flex;
                    align-items: center;
                    gap: 8px;">
            <span style="
                      width: 3px;
                      height: 18px;
                      background: linear-gradient(180deg, #1e40af 0%, #0d9488 100%);
                      border-radius: 2px;
                      display: inline-block;
                    ">
            </span>
            👨‍⚕️ Prescriber Information
        </div>
        <div class="details-grid" style="
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 12px;
                    margin-top: 2px;
                ">
            <div class="detail-item" style="display: flex;
                        flex-direction: column;
                        gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                        color: #6b7280;
                        font-weight: 500;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;">Prescriber Name
                </div>
                <div class="detail-value" style="font-size: 14px;
                        color: #1f2937;
                        font-weight: 500;
                        padding: 4px 0;">${escapeHtml(
                        data?.pharmacistInformation?.createdByName ??
                          data?.userDetails?.fullName ??
                          "",
                      )}
                </div>
            </div>
            <div class="detail-item" style="display: flex;
                        flex-direction: column;
                        gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                        color: #6b7280;
                        font-weight: 500;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;">Email Address
                </div>
                <div class="detail-value" style="font-size: 14px;
                        color: #1f2937;
                        font-weight: 500;
                        padding: 4px 0;">${escapeHtml(
                              data?.pharmacistInformation?.createdBy ??
                                data?.userDetails?.emailAddress ??
                                "",
                            )}
                </div>
            </div>
            <div class="detail-item" style="display: flex;
                        flex-direction: column;
                        gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                        color: #6b7280;
                        font-weight: 500;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;">Authorisation Code
                </div>
                <div class="detail-value auth-code" style="font-size: 14px;
                        color: #1f2937;
                        font-weight: 500;
                        padding: 4px 0; font-family: 'JetBrains Mono', monospace;
                        font-weight: 600;
                        color: #1e40af;
                        background: #ffffff;
                        padding: 4px 8px;
                        border-radius: 6px;
                        border: 1px solid #e5e7eb;">RX-${data?.authorizationCode}
                </div>
            </div>
        </div>
    </div>
    <div class="section" style="padding: 18px 25px;
                          border-bottom: 1px solid #e5e7eb;
                          position: relative;
                          z-index: 2;">
        <div class="section-title" style="font-size: 15px;
                        display: flex; align-items: center; gap: 8px;
                        color: #1e40af;
                        font-weight: 600;
                        margin-bottom: 12px;
                        padding-bottom: 6px;
                        border-bottom: 2px solid #f0f9ff;
                        display: flex;
                        align-items: center;
                        gap: 8px;">
            <span style="
                            width: 3px;
                            height: 18px;
                            background: linear-gradient(180deg, #1e40af 0%, #0d9488 100%);
                            border-radius: 2px;
                            display: inline-block;
                        ">
            </span>
            👤 Patient Information
        </div>
        <div class="details-grid" style="
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 12px;
                    margin-top: 2px;
                ">
            <div class="detail-item" style="display: flex;
                    flex-direction: column;
                    gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                    color: #6b7280;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;">Patient Name
                </div>
                <div class="detail-value" style="font-size: 14px;
                    color: #1f2937;
                    font-weight: 500;
                    padding: 4px 0;">
                    ${escapeHtml(data?.patientDetails?.title) ?? ""}
                    ${escapeHtml(data?.patientDetails?.name) ?? "Unknown"}
                </div>
            </div>
            <div class="detail-item" style="display: flex;
                    flex-direction: column;
                    gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                    color: #6b7280;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;">Date of Birth
                </div>
                <div class="detail-value" style="font-size: 14px;
                    color: #1f2937;
                    font-weight: 500;
                    padding: 4px 0;">
                    ${escapeHtml(data?.patientDetails?.dob) ?? "Unknown"}
                </div>
            </div>
${
  data?.patientDetails?.phone
    ? `
<div class="detail-item" style="display: flex; flex-direction: column; gap: 2px;">
  <div class="detail-label" style="font-size: 11px;
      color: #6b7280;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;">
    Phone Number
  </div>
  <div class="detail-value" style="font-size: 14px;
      color: #1f2937;
      font-weight: 500;
      padding: 4px 0;">
    ${escapeHtml(data.patientDetails.phone)}
  </div>
</div>
`
    : ""
}

            <div class="detail-item" style="display: flex;
                    flex-direction: column;
                    gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                    color: #6b7280;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;">Known Allergies
                </div>
                <div class="detail-value" style="font-size: 14px;
                    color: #1f2937;
                    font-weight: 500;
                    padding: 4px 0;">
                    ${
                      escapeHtml(data?.patientDetails?.allergies) ??
                      "No known allergies"
                    }
                </div>
            </div>
             <div class="detail-item" style="display: flex;
                    flex-direction: column;
                    gap: 2px;">
                <div class="detail-label" style="font-size: 11px;
                    color: #6b7280;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;">Address
                </div>
                <div class="detail-value" style="font-size: 14px;
                    color: #1f2937;
                    font-weight: 500;
                    padding: 4px 0;">
                    ${escapeHtml(data?.patientDetails?.address) ?? "Unknown"}
                </div>
            </div>
        </div>
    </div>
    <div class="section" style="padding: 18px 25px;
                    border-bottom: 1px solid #e5e7eb;
                    position: relative;
                    z-index: 2;">
        <div class="section-title" style="font-size: 15px;
                        display: flex; align-items: center; gap: 8px;
                        color: #1e40af;
                        font-weight: 600;
                        margin-bottom: 12px;
                        padding-bottom: 6px;
                        border-bottom: 2px solid #f0f9ff;
                        display: flex;
                        align-items: center;
                        gap: 8px;">
            <span style="
                            width: 3px;
                            height: 18px;
                            background: linear-gradient(180deg, #1e40af 0%, #0d9488 100%);
                            border-radius: 2px;
                            display: inline-block;
                            ">
            </span>
             ${getItemsSectionTitle(data?.prescriptionType)}
        </div>
        <table class="prescription-table" style="
                        width: 100%;
                        border-collapse: separate;
                        border-spacing: 0;
                        margin-top: 12px;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
                        page-break-inside: avoid !important;
                      ">
            <thead>
                <tr>
                    <th style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
                        color: white;
                        padding: 10px 12px;
                        text-align: left;
                        font-size: 12px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border: none;">Medication &amp; Strength
                    </th>
                    <th style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
                        color: white;
                        padding: 10px 12px;
                        text-align: left;
                        font-size: 12px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border: none;">Quantity
                    </th>
                    <th style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
                        color: white;
                        padding: 10px 12px;
                        text-align: left;
                        font-size: 12px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border: none;">Dosage Instructions
                    </th>
                </tr>
            </thead>
            <tbody>
            <tbody>
                ${
                  (data?.prescriptionInfo || [])
                    .map(
                      (item, index) => `
                <tr key=${index}>
                    <td 
                    style="padding: 10px; border: 1px solid #ddd; font-family: Arial, Helvetica, sans-serif; word-break: keep-all; max-width: 250px;">
                    ${
                      escapeHtml(
                        [item?.drugName, item?.strength, item?.medicationType]
                          .filter(Boolean)
                          .join(" "),
                      ) ?? "Unknown"
                    }</td>
                    <td style="padding: 10px; border: 1px solid #ddd;">${
                      escapeHtml(item?.quantity) ?? "Unknown"
                    }</td>
                    <td style="padding: 10px; border: 1px solid #ddd; word-break: break-word; white-space: normal; max-width: 300px;">
                        <div style="font-weight: 500; margin-bottom: 2px;">${escapeHtml(item?.dose) ?? "Unknown"}</div>
                        ${item?.note ? `<div style="font-size: 11px; color: #4b5563; line-height: 1.3;">${escapeHtml(item.note)}</div>` : ""}
                    </td>
                </tr>`,
                    )
                    .join("") ||
                  `
                <tr>
                    <td colspan="6" style="text-align:center; padding:12px;">No Prescription Info Found</td>
                </tr>`
                }
            </tbody>
            </tbody>
        </table>
    </div>
    <div class="digital-signature" style="background: #f0f9ff;
                    padding: 16px 25px;
                    text-align: center;
                    border-top: 2px solid #3b82f6;
                    position: relative;
                    z-index: 2;">
        <div class="signature-content" style="max-width: 500px;
                    margin: 0 auto;">
            <div class="signature-icon" style="width: 32px;
                    height: 32px;
                    background: #1e40af;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 8px;
                    color: white;
                    font-size: 16px;">
                ✓
            </div>
            <div class="signature-text" style="font-size: 13px;
                    color: #4b5563;
                    margin-bottom: 8px;
                    line-height: 1.5;">
                This prescription was digitally signed on
              <strong>
                ${escapeHtml(formatDate(data?.prescriptionDate))}
              </strong>
                and is valid without a handwritten signature Images.
            </div>
            <div class="signature-details" style="font-size: 12px;
                    color: #6b7280;
                    font-weight: 500;">
                Digital Authorisation Code:
                <span class="auth-code" style="font-family: 'JetBrains Mono', monospace;
                    font-weight: 600;
                    color: #1e40af;
                    background: #ffffff;
                    padding: 4px 8px;
                    border-radius: 6px;
                    border: 1px solid #e5e7eb;">${escapeHtml(
                      data?.authorizationCode,
                    )}
                </span>
            </div>
        </div>
    </div>
    <div class="footer" style="
                background: #1f2937;
                color: white;
                padding: 12px 25px;
                text-align: center;
                font-size: 10px;
                line-height: 1.4;
                position: relative;
                z-index: 2;
            ">
        <div class="footer-content" style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    max-width: 500px;
                    margin: 0 auto;
                    flex-wrap: wrap;
                    gap: 12px;
                ">
            <div class="page-info" style="color: #9ca3af;">
                Page 1 of 1
            </div>
            <div class="compliance-info" style="
                    color: #d1d5db;
                    font-weight: 500;
                    font-size: 9px;
                    ">
                This prescription is valid for ${data?.prescriptionType} dispensing only. Ensure compliance with all applicable
                regulations.
            </div>
        </div>
    </div>
</div>
`;
};
